import React, { useState, useRef, useEffect, useImperativeHandle } from "react";

const OTPInput = ({ onSubmit, ref, isDisabled = false, otpValidationState, OTP_VALIDATION_STATE_INCORRECT }) => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  useImperativeHandle(ref, () => {
    return {
      reset() {
        setOtp(["", "", "", ""]);
      },
      submit() {
        handleSubmit();
      },
    };
  });

  useEffect(() => {
    // Check if all OTP fields are filled
    const isOtpComplete = otp.every((digit) => digit !== "");

    if (isOtpComplete) {
      onSubmit(otp.join(""));
    }
  }, [otp]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 4);
  }, []);

  const handleChange = (index, e) => {
    const value = e.target.value;

    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    // Only take the last character if somehow multiple are pasted
    const singleDigit = value.substring(value.length - 1);

    // Update OTP state
    const newOtp = [...otp];
    newOtp[index] = singleDigit;
    setOtp(newOtp);

    // Move to next input if current field is filled
    if (singleDigit && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current field is empty
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Filter out non-numeric characters
    const numericData = pastedData.replace(/\D/g, "");

    // Fill the OTP fields with pasted data
    const newOtp = [...otp];
    for (let i = 0; i < Math.min(numericData.length, 4); i++) {
      newOtp[i] = numericData[i];
    }
    setOtp(newOtp);

    // Focus on the next empty field or the last field
    const nextEmptyIndex = newOtp.findIndex((val) => val === "");
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex].focus();
    } else if (newOtp[3]) {
      inputRefs.current[3].focus();
    }
  };

  const handleSubmit = () => {
    const otpValue = otp.join("");
    if (otpValue.length === 4) {
      onSubmit(otpValue);
      // You can replace this with your API call or validation logic
    }
  };

  return (
    <div className="flex gap-2">
      {otp.map((digit, index) => (
        <input
          autoFocus={index === 0}
          key={index}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : null}
          ref={(el) => (inputRefs.current[index] = el)}
          className={`w-9 h-10 relative bg-white rounded-[10px] border-[1.50px]
          border-${otpValidationState !== OTP_VALIDATION_STATE_INCORRECT ? 'violet-600 border-opacity-20' : '[#BC0000]'}
          text-center font-bold focus:outline-none
          disabled:bg-[#E5E5E5] disabled:border-transparent disabled:text-[#666666] disabled:cursor-not-allowed`}
          disabled={isDisabled}
        />
      ))}
      <br />
    </div>
  );
};

export default OTPInput;
